import React, { useMemo } from 'react';
import LiteBar from './LiteBar';
import { ActiveFunctions, FunctionType, TimelineItem, TimelineViewport } from '@/types/item';
import { getActiveGates } from '../utils/liteTimelineUtils';
import { toDateKey } from '@/utils/dateTZ';

// Simple resource conflict check for lite version
const isResourceConflictDay = (date: Date, conflictDays?: string[]): boolean => {
  if (!conflictDays) return false;
  // ✅ Timezone-aware date key (prevents 1-day shift)
  const dateStr = toDateKey(date);
  return conflictDays.includes(dateStr);
};

interface LiteItemRowProps {
  item: TimelineItem;
  activeFunctions: ActiveFunctions;
  viewport: TimelineViewport;
  dateColumns: Date[];
  isMonthStart: (date: Date) => boolean;
  isWeekend: (date: Date) => boolean;
  isToday: (date: Date) => boolean;
}

const cellWidth = 32;

const LiteItemRow: React.FC<LiteItemRowProps> = ({
  item,
  activeFunctions,
  viewport,
  dateColumns,
  isMonthStart,
  isWeekend,
  isToday,
}) => {
  const activeFunctionTypes = useMemo(
    () => Object.entries(activeFunctions).filter(([, v]) => v).map(([k]) => k as FunctionType),
    [activeFunctions]
  );

  const lanes = activeFunctionTypes.length || 1;
  const laneHeight = 10;
  const rowHeight = 6 + lanes * laneHeight;
  const totalWidth = dateColumns.length * cellWidth;

  // Calculate item completion percentage
  const itemCompletion = useMemo(() => {
    return item.completion_percentage ?? 0;
  }, [item.completion_percentage]);

  // Determine completion color based on status
  const completionColorClass = useMemo(() => {
    if (itemCompletion === 0) return '';
    if (itemCompletion === 100) return 'bg-green-500/15';
    if (item.manual_completion_percentage !== null && item.manual_completion_percentage !== undefined) {
      return 'bg-orange-500/10'; // Manual override
    }
    return 'bg-primary/10'; // Auto-calculated
  }, [itemCompletion, item.manual_completion_percentage]);

  return (
    <div className="flex border-b">
      {/* Left info column (name) */}
      <div className="w-64 shrink-0 px-3 py-2 text-sm text-foreground/90">{item.name}</div>
      {/* Timeline grid + overlay */}
      <div className="relative" style={{ width: totalWidth, height: rowHeight }}>
        {/* Item progress background */}
        {itemCompletion > 0 && (
          <div 
            className={`absolute inset-y-0 left-0 transition-all ${completionColorClass}`}
            style={{ 
              width: `${itemCompletion}%`,
              zIndex: 5 
            }}
          />
        )}
        {/* Background grid */}
        <div className="absolute inset-0" style={{ zIndex: 10 }}>
          <div className="flex h-full">
            {dateColumns.map((date) => {
              const isConflict = isResourceConflictDay(date, item.resourceConflictDays);
              return (
                <div
                  key={date.toISOString()}
                  className={`w-8 h-full border-r relative ${isMonthStart(date) ? 'border-l-2 border-l-primary' : ''} ${isWeekend(date) ? 'bg-muted/20' : ''} ${isToday(date) ? 'bg-primary/10' : ''} ${isConflict ? 'bg-destructive/10' : ''}`}
                >
                  {isConflict && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-destructive" />}
                </div>
              );
            })}
          </div>
        </div>
        {/* Bars overlay */}
        <div className="absolute inset-0" style={{ zIndex: 20 }}>
          {activeFunctionTypes.map((functionType, laneIndex) => {
            const functionData = item.timelineData[functionType];
            const gates = getActiveGates(functionData);
            return gates.map((gate, gateIndex) => (
              <LiteBar
                key={`${item.id}-${functionType}-${gate.gate}`}
                dateRange={gate.dateRange}
                functionType={functionType}
                viewportStart={viewport.startDate}
                viewportEnd={viewport.endDate}
                height={8}
                top={2 + laneIndex * laneHeight + gateIndex * 2}
                hasWarning={functionData.hasG4Warning && gate.gate === 'g4'}
                isComplete={functionData.isG5Complete && gate.gate === 'g5'}
              />
            ));
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(LiteItemRow);
